/**
 * Shared formatting utilities for the Profitcast Dashboard.
 * Single source of truth — import from here, never duplicate.
 */

/**
 * Format a monetary value in Indian units (L = lakhs, K = thousands).
 * @param {number|null|undefined} amount
 * @param {string} [currency='INR']
 * @returns {string}
 */
export const formatCurrency = (amount, currency = 'INR') => {
  if (amount === null || amount === undefined || isNaN(amount)) return '—';
  const symbol = currency === 'USD' ? '$' : '₹';
  if (amount >= 100000) return `${symbol}${(amount / 100000).toFixed(1)}L`;
  if (amount >= 1000) return `${symbol}${(amount / 1000).toFixed(1)}K`;
  return `${symbol}${Number(amount).toFixed(0)}`;
};

/**
 * Format a large number with K/M suffix.
 * @param {number|null|undefined} num
 * @returns {string}
 */
export const formatNumber = (num) => {
  if (num === null || num === undefined || isNaN(num)) return '—';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return String(Math.round(num));
};

/**
 * Format a percentage with one decimal place.
 * @param {number|null|undefined} value
 * @returns {string}
 */
export const formatPercent = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '—';
  return `${Number(value).toFixed(1)}%`;
};

/**
 * Format an ISO date string to a human-readable date.
 * @param {string|null|undefined} isoString
 * @returns {string}
 */
export const formatDate = (isoString) => {
  if (!isoString) return '—';
  try {
    return new Date(isoString).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  } catch {
    return isoString;
  }
};

/**
 * Format an ISO date string to a human-readable date + time.
 * @param {string|null|undefined} isoString
 * @returns {string}
 */
export const formatDateTime = (isoString) => {
  if (!isoString) return '—';
  try {
    return new Date(isoString).toLocaleString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return isoString;
  }
};

/**
 * Format a relative time (e.g., "2 hours ago").
 * @param {string|Date|null|undefined} dateInput
 * @returns {string}
 */
export const formatRelativeTime = (dateInput) => {
  if (!dateInput) return '—';
  try {
    const date = new Date(dateInput);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  } catch {
    return '—';
  }
};
